/* eslint-disable jsx-a11y/no-static-element-interactions */
import styled from '@emotion/styled';
import { useCallback, useContext, useState } from 'react';
import type { ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';

import ChefronLeftIcon from 'ms-components/icons/ChevronLeft';
import EngageTaskIcon from 'ms-components/icons/EngageTask';
import InfoFilledIcon from 'ms-components/icons/InfoFilled';
import PaperSheetIcon from 'ms-components/icons/PaperSheet';
import PencilIcon from 'ms-components/icons/Pencil';
import TextbookTeacherIcon from 'ms-components/icons/TextbookTeacher';
import WorksheetIcon from 'ms-components/icons/Worksheet';
import Logo from 'ms-components/logos/Logo';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import {
  FreeTextbooksTopicSubtopicTree,
  type Topic,
  type Subtopic,
} from 'ms-pages/FreeTextbooks/FreeTextbooksTopicSubtopicTree';
import {
  HORIZONTAL_PADDING,
  LEFT_ASIDE_ELEMENT_VERTICAL_PADDING,
  MIN_SCREEN_WIDTH_FOR_SIDEBAR,
} from 'ms-pages/FreeTextbooks/Layout/constants';
import {
  freeTextbooksRootSelector,
  getFreeTextbooksSubtopicDetailViewUrl,
  getFreeTextbooksTopicEmptyViewUrl,
} from 'ms-pages/FreeTextbooks/urls';
import { ACCORDION_ANIMATION_DURATION } from 'ms-pages/Teacher/components/Accordion';
import { FlyoutContext } from 'ms-pages/Teacher/components/Flyout';
import { SelectableListItem } from 'ms-pages/Teacher/components/SelectableList';
import {
  type CoreTextbookSubtopicActiveTab,
  type LegacyTextbookSubtopicActiveTab,
  validateCoreTextbookSubtopicActiveTab,
  validateLegacyTextbookSubtopicActiveTab,
} from 'ms-pages/Textbooks/components/SubtopicDetailView/helpers';
import {
  font,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
} from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import AnchorButton from 'ms-ui-primitives/Button/AnchorButton';
import LinkButton from 'ms-ui-primitives/Button/ReactRouterLinkButton';
import HighLighter from 'ms-ui-primitives/Highlighter';
import SearchInput from 'ms-ui-primitives/SearchInput';
import Separator from 'ms-ui-primitives/Separator';
import {
  accessibilityModeStyle,
  useAccessibilityMode,
} from 'ms-utils/accessibility';
import {
  onPressOrHover,
  styledVerticallyScrollable,
  tappable,
} from 'ms-utils/emotion';
import useQueryParam from 'ms-utils/hooks/useQueryParam';
import { useRouter } from 'ms-utils/hooks/useRouter';
import type { Action } from 'ms-utils/keyDownMap';
import keyDownMap from 'ms-utils/keyDownMap';
import _ from 'ms-utils/relay/extractNode';
import { assertUnreachable } from 'ms-utils/typescript-utils';
import { deserialize, serialize } from 'ms-utils/urls/queryParams';

import { FreeTextbooksSelectorButton } from './FreeTextbooksSelectorButton/FreeTextbooksSelectorButton';

type Props = {
  syllabusId: string;
  activeTopicId: string | null;
  activeSubtopicId: string | null;
  searchString: string;
};

type TitleStyleOptions = {
  active: boolean;
  accessibilityMode: boolean;
};
const topicTitleStyle = [
  {
    fontFamily: fontFamily.body,
    fontSize: fontSize.medium,
    fontWeight: fontWeight.normal,
    textDecoration: 'none',
    color: colors.shuttleGray,
    padding: BASE_UNIT,
    lineHeight: lineHeight.body,
    display: 'block',
    ...tappable,
  },
  ({ active }: TitleStyleOptions) =>
    active && {
      color: colors.cloudBurst,
      fontWeight: fontWeight.semibold,
    },
  ({ accessibilityMode }: TitleStyleOptions) =>
    accessibilityMode && accessibilityModeStyle,
];
const TopicTitleLink = styled(Link, {
  shouldForwardProp: (prop): prop is keyof ComponentProps<typeof Link> =>
    prop !== 'active' && prop !== 'accessibilityMode',
})<TitleStyleOptions>(...topicTitleStyle);

const TopicTitleNotLink = styled.div<TitleStyleOptions>(...topicTitleStyle);

const TopicsWrapper = styled.div({
  paddingLeft: HORIZONTAL_PADDING,
  paddingRight: HORIZONTAL_PADDING,
  paddingBottom: LEFT_ASIDE_ELEMENT_VERTICAL_PADDING,
  flexGrow: 1,
  marginRight: BASE_UNIT, // to leave a small whitespace between the scrollbar and sidebar edge
  ...styledVerticallyScrollable,
});

const SubtopicItem = styled.div({
  padding: `${BASE_UNIT} 0 ${BASE_UNIT} ${3 * BASE_UNIT}`,
  lineHeight: lineHeight.body,
  fontSize: fontSize.medium,
});

const SearchWrapper = styled.div({
  paddingLeft: HORIZONTAL_PADDING,
  paddingRight: HORIZONTAL_PADDING,
  paddingBottom: LEFT_ASIDE_ELEMENT_VERTICAL_PADDING,
});
const FreeTextbooksSelectorButtonWrapper = styled.div({
  paddingLeft: HORIZONTAL_PADDING,
  paddingRight: HORIZONTAL_PADDING,
  paddingTop: LEFT_ASIDE_ELEMENT_VERTICAL_PADDING,
  paddingBottom: LEFT_ASIDE_ELEMENT_VERTICAL_PADDING,
});

const LinkBackToTopicsButtonWrapper = styled.div({
  paddingTop: LEFT_ASIDE_ELEMENT_VERTICAL_PADDING,
  flexShrink: 0,
});

const LinkBackToTopicsButtonStyle = {
  button: {
    fontSize: fontSize.medium,
    color: colors.eggplant,
  },
};

const SidebarTitle = styled.div({
  paddingLeft: HORIZONTAL_PADDING,
  paddingRight: HORIZONTAL_PADDING,
  paddingTop: LEFT_ASIDE_ELEMENT_VERTICAL_PADDING / 2,
  ...font.gilroyLargerHeading,
  [`@media(min-width: ${MIN_SCREEN_WIDTH_FOR_SIDEBAR}px)`]: {
    display: 'none',
  },
});

const BrandWrapper = styled.div({
  paddingLeft: HORIZONTAL_PADDING,
  paddinRight: HORIZONTAL_PADDING,
  paddingTop: HORIZONTAL_PADDING,
  display: 'flex',
  width: '100%',
  [`@media(max-width: ${MIN_SCREEN_WIDTH_FOR_SIDEBAR + 1}px)`]: {
    display: 'none',
  },
});

const captureElementForScroll = (element: HTMLElement) => () => {
  scrollIntoView(element, {
    scrollMode: 'if-needed',
    block: 'center',
    inline: 'nearest',
    skipOverflowHiddenElements: true,
    boundary: parent => {
      // This is to make sure that we don't scroll the body
      if (document && document.body) {
        return parent !== document.body;
      }
      return true;
    },
  });
};

export default function FreeTextbooksTopicSidebar({
  activeTopicId,
  activeSubtopicId,
  searchString,
  syllabusId,
}: Props) {
  const {
    location: { pathname, search: queryString },
    history: { replace, push },
  } = useRouter();
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(
    activeTopicId,
  );
  const [hasEnabledAccessibilityMode] = useAccessibilityMode();

  const createSubtopicNavLink = (topicId: string, subtopicId: string) =>
    getFreeTextbooksSubtopicDetailViewUrl({
      syllabusId,
      topicId,
      subtopicId,
    });

  const { trackStructEvent } = useSnowplow();

  const getTopicLink = useCallback(
    (topic: Topic) => {
      // This is to stop collapsing a group from potentially navigating to a different topic

      if (topic.id === activeTopicId) return '#';
      return {
        pathname: getFreeTextbooksTopicEmptyViewUrl({
          syllabusId,
          topicId: topic.id,
        }),
        search: queryString,
      };
    },
    [activeTopicId, queryString, syllabusId],
  );

  const onTopicSelect = useCallback<(topic: Topic) => Action<HTMLElement>>(
    topic => ev => {
      if (activeSubtopicId != null) return;
      setExpandedTopicId(prev => (prev === topic.id ? null : topic.id));
      trackStructEvent({
        category: 'free_textbook',
        action: 'select_topic',
        property: 'topicId',
        label: topic.id,
      });
      setTimeout(
        captureElementForScroll(ev.currentTarget),
        // allow ample time for the animation to complete
        ACCORDION_ANIMATION_DURATION * 2,
      );

      push(getTopicLink(topic));
    },
    [activeSubtopicId, getTopicLink, push, trackStructEvent],
  );

  return (
    <>
      <BrandWrapper>
        <AnchorButton href={freeTextbooksRootSelector} label="Home" padding={0}>
          <Logo hasText />
        </AnchorButton>
      </BrandWrapper>
      {activeSubtopicId == null && <SidebarTitle>Select a topic</SidebarTitle>}
      {activeSubtopicId != null && activeTopicId != null && (
        <LinkBackToTopicsButtonWrapper>
          <LinkButton
            styles={LinkBackToTopicsButtonStyle.button}
            to={getFreeTextbooksTopicEmptyViewUrl({
              syllabusId,
              topicId: activeTopicId,
            })}
            label="Go back to topics list"
          >
            <ChefronLeftIcon />
            <Separator size={1} />
            Topics
          </LinkButton>
        </LinkBackToTopicsButtonWrapper>
      )}
      {activeSubtopicId == null && (
        <FreeTextbooksSelectorButtonWrapper>
          <FreeTextbooksSelectorButton syllabusId={syllabusId} />
        </FreeTextbooksSelectorButtonWrapper>
      )}
      {activeSubtopicId == null && (
        <SearchWrapper>
          <SearchInput
            searchString={searchString}
            placeholder="Search for a subtopic"
            whiteBackground
            onSubmit={newSearchString => {
              trackStructEvent({
                category: 'free_textbook',
                action: 'use_subtopic_search',
                property: 'searchString',
                label: newSearchString,
              });
              replace({
                pathname,
                search: serialize({
                  ...deserialize(queryString),
                  searchString: newSearchString, // Push explicitly empty search string.
                  // Within queryParamState, we spread in new values to shadow old values,
                  // which means to shadow a previous search string with a new search string spread over the
                  // top, we need the key to be present.  Otherwise, the old value will not
                  // be masked, and some state that we'd intended to clear will persist.
                }),
              });
            }}
          />
        </SearchWrapper>
      )}
      <TopicsWrapper>
        <FreeTextbooksTopicSubtopicTree
          isTopicOpen={topicId => expandedTopicId === topicId}
          syllabusId={syllabusId}
          search={searchString}
          color={colors.grayChateau}
          topicFilter={topic => _(topic.subtopics).length > 0}
          renderTopicLabel={(topic, search) => {
            if (activeSubtopicId != null && activeTopicId !== topic.id)
              return null;
            const Title =
              topic.id === activeTopicId ? TopicTitleNotLink : TopicTitleLink;
            return (
              <Title
                accessibilityMode={hasEnabledAccessibilityMode}
                active={topic.id === activeTopicId}
                onClick={onTopicSelect(topic)}
                to={getTopicLink(topic)}
                onKeyDown={keyDownMap({
                  ENTER: [onTopicSelect(topic), { preventDefault: true }],
                  SPACE: [onTopicSelect(topic), { preventDefault: true }],
                })}
              >
                <HighLighter highlights={search.split(' ')}>
                  {topic.title}
                </HighLighter>
              </Title>
            );
          }}
          renderSubtopicLabel={(topic, subtopic, search) => (
            <>
              <SelectableListItem
                accessibilityMode={hasEnabledAccessibilityMode}
                key={subtopic.id}
                onComponentMount={node => {
                  if (subtopic.id === activeSubtopicId)
                    setTimeout(
                      captureElementForScroll(node),
                      ACCORDION_ANIMATION_DURATION,
                    );
                }}
                to={{
                  pathname: createSubtopicNavLink(topic.id, subtopic.id),
                  search: queryString,
                }}
                active={activeSubtopicId === subtopic.id}
                onClick={ev => {
                  trackStructEvent({
                    category: 'free_textbook',
                    action: 'select_subtopic',
                    property: 'subtopicId',
                    label: subtopic.id,
                  });
                  setTimeout(
                    captureElementForScroll(ev.currentTarget),
                    ACCORDION_ANIMATION_DURATION,
                  );
                }}
                renderOnActive={() => (
                  <>
                    <SubtopicMenu
                      textbookType={topic.syllabus.textbookType}
                      subtopic={subtopic}
                    />
                  </>
                )}
              >
                <SubtopicItem>
                  <HighLighter highlights={search.split(' ')}>
                    {subtopic.title}
                  </HighLighter>
                </SubtopicItem>
              </SelectableListItem>
            </>
          )}
          renderTopicOverviewLabel={() => null}
        />
      </TopicsWrapper>
    </>
  );
}

const LightGrayBox = styled.div({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 3 * BASE_UNIT,
  background: colors.iron,
  padding: 3 * BASE_UNIT,
});

const SubtopicMenuItemElement = styled(Link, {
  shouldForwardProp: (prop): prop is keyof ComponentProps<Link> =>
    prop !== 'active',
})<{
  active: boolean;
}>(
  {
    fontFamily: fontFamily.body,
    fontWeight: fontWeight.normal,
    fontSize: fontSize.medium,
    lineHeight: lineHeight.body,
    color: colors.shuttleGray,
    display: 'flex',
    alignItems: 'center',
    height: 6 * BASE_UNIT,
    textDecoration: 'none',
    ...tappable,
    ...onPressOrHover({
      opacity: 0.8,
    }),
  },
  ({ active }) =>
    active && { color: colors.cloudBurst, fontWeight: fontWeight.semibold },
);

const SUBTOPIC_MENU_ITEM_ICON_SIZE = 16;

function SubtopicMenu({
  textbookType,
  subtopic,
}: {
  textbookType: Topic['syllabus']['textbookType'];
  subtopic: Subtopic;
}) {
  const { hasLessons, hasInvestigations, hasWorksheet, hasExercise } = subtopic;
  const hasTheory = hasLessons || hasInvestigations;
  const hasAnything = hasTheory || hasWorksheet || hasExercise;

  return hasAnything ? (
    <LightGrayBox>
      {/* TODO should probably be exhaustive switch as we're missing a textbookType now */}
      {textbookType === 'LEGACY' && (
        <>
          {hasTheory && <SubtopicMenuItem resource="theory" />}
          {hasWorksheet && <SubtopicMenuItem resource="worksheet" />}
          {hasExercise && <SubtopicMenuItem resource="interactive" />}
        </>
      )}
      {textbookType === 'CORE_TEXTBOOK' && (
        <>
          <CoreTextbookSubtopicMenuItem coreTextbookResource="solidifyLesson" />
          <CoreTextbookSubtopicMenuItem coreTextbookResource="interactive" />
        </>
      )}
    </LightGrayBox>
  ) : null;
}

type SubtopicMenuItemProps = {
  resource: LegacyTextbookSubtopicActiveTab;
};

function SubtopicMenuItem({ resource }: SubtopicMenuItemProps) {
  const [activeTab, , getTabLink] =
    useQueryParam<LegacyTextbookSubtopicActiveTab>(
      'activeTab',
      'theory',
      validateLegacyTextbookSubtopicActiveTab,
    );

  const link = getTabLink(resource);

  const {
    history: { push },
  } = useRouter();

  // if the sidebar is rendered in a flyout,
  // dismiss it when a subtopic resource is clicked/tapped
  const { closeFlyout } = useContext(FlyoutContext);
  const action = useCallback(() => {
    push(link);
    closeFlyout();
  }, [closeFlyout, link, push]);

  if (resource === null) return null;

  const { Icon, label } = getSubtopicResource(resource);
  return (
    <SubtopicMenuItemElement
      active={activeTab === resource}
      to={link}
      onClick={action}
      onKeyDown={keyDownMap({
        ENTER: [action, { preventDefault: true }],
        SPACE: [action, { preventDefault: true }],
      })}
    >
      <Icon size={SUBTOPIC_MENU_ITEM_ICON_SIZE} />
      <Separator size={2} />
      {label}
    </SubtopicMenuItemElement>
  );
}

type CoreTextbookSubtopicMenuItemProps = {
  coreTextbookResource: CoreTextbookSubtopicActiveTab;
};

function CoreTextbookSubtopicMenuItem({
  coreTextbookResource,
}: CoreTextbookSubtopicMenuItemProps) {
  const [coreTextbookSubtopicActiveTab, , getTabLink] =
    useQueryParam<CoreTextbookSubtopicActiveTab>(
      'coreTextbookSubtopicActiveTab',
      'solidifyLesson',
      validateCoreTextbookSubtopicActiveTab,
    );

  const link = getTabLink(coreTextbookResource);

  const {
    history: { push },
  } = useRouter();

  // if the sidebar is rendered in a flyout,
  // dismiss it when a subtopic resource is clicked/tapped
  const { closeFlyout } = useContext(FlyoutContext);
  const action = useCallback(() => {
    push(link);
    closeFlyout();
  }, [closeFlyout, link, push]);

  const { Icon, label } =
    useGetCoreTextbookSubtopicResource(coreTextbookResource);
  return (
    <SubtopicMenuItemElement
      active={
        coreTextbookSubtopicActiveTab === 'worksheet'
          ? coreTextbookResource === 'interactive'
          : coreTextbookSubtopicActiveTab === coreTextbookResource
      }
      to={link}
      onClick={action}
      onKeyDown={keyDownMap({
        ENTER: [action, { preventDefault: true }],
        SPACE: [action, { preventDefault: true }],
      })}
    >
      <Icon size={SUBTOPIC_MENU_ITEM_ICON_SIZE} />
      <Separator size={2} />
      {label}
    </SubtopicMenuItemElement>
  );
}

function getSubtopicResource(
  activeTab: NonNullable<LegacyTextbookSubtopicActiveTab>,
) {
  switch (activeTab) {
    case 'theory':
      return {
        Icon: TextbookTeacherIcon,
        label: 'Lesson',
      };
    case 'worksheet':
      return {
        Icon: WorksheetIcon,
        label: 'Worksheet',
      };
    case 'interactive':
      return {
        Icon: PencilIcon,
        label: 'Practice',
      };
    case 'teacherNotes':
      return {
        Icon: PaperSheetIcon,
        label: 'Teacher notes',
      };

    case 'engageActivity':
      return {
        Icon: EngageTaskIcon,
        label: 'Engage',
      };

    default: {
      assertUnreachable(activeTab, `activeTab not recognised: ${activeTab}`);
    }
  }
}

function useGetCoreTextbookSubtopicResource(
  coreTextbookActiveTab: CoreTextbookSubtopicActiveTab,
) {
  switch (coreTextbookActiveTab) {
    case 'subtopicOverview':
      return {
        Icon: InfoFilledIcon,
        label: 'Overview',
      };
    case 'solidifyLesson':
      return {
        Icon: TextbookTeacherIcon,
        label: 'Lesson',
      };
    case 'engageActivity':
      return {
        Icon: EngageTaskIcon,
        label: 'Engage',
      };
    case 'worksheet':
      return {
        Icon: WorksheetIcon,
        label: 'Worksheet',
      };
    case 'interactive':
      return {
        Icon: PencilIcon,
        label: 'Practice',
      };

    default: {
      assertUnreachable(
        coreTextbookActiveTab,
        `coreTextbookActiveTab not recognised: ${coreTextbookActiveTab}`,
      );
    }
  }
}
